import { useLocation, useNavigate } from "react-router";
import { formatDateInTimeZone } from "../../../../../../lib/formatDateInTimeZone";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/reduxToolkit";
import { useUser } from "../../../../../hooks/useUser";
import { completeSubmission } from "../../../../../reduxToolkit/actions/completeSubmission";
import { SubmissionById } from "../../../../../reduxToolkit/selectors/submissionById";
import { SubmissionData2026CHM, SubmissionData2026CHMAddon } from "../../../../../reduxToolkit/submissionsSlice";
import { emailMyself } from "@healthshare/shared/commands/support";
import { useAsyncAction } from "../../../../../hooks/useAsyncAction";
import { useBackendApi } from "../../../../../hooks/useBackendApi";
import { useClient } from "../../../../../providers/supabase";
import { downloadBlob } from "../../../../../../lib/util/downloadBlob";
import Toast from 'react-hot-toast';
import { downloadBlobsAsZip } from "../../../../../../lib/util/downloadBlobsAsZip";
import { downloadFileNameForBlob } from "../../../../../../lib/util/convertImageBlobToPdf";
import { SubmissionExpenseList } from "./submissionExpenseList";
import { isMobile } from "../../../../../../lib/util/isMobile";
import { useSentry } from "../../../../../providers/sentry";

interface SubmissionInstructions2026Props {
  submission: SubmissionById
  data: SubmissionData2026CHM | SubmissionData2026CHMAddon
}

export function SubmissionInstructions2026({ submission, data }: SubmissionInstructions2026Props) {
  const dispatch = useAppDispatch()
  const user = useUser()
  const navigate = useNavigate()
  const client = useClient()
  const api = useBackendApi()
  const location = useLocation()
  const mobile = isMobile()
  const { captureException } = useSentry()

  // Email me a link functionality
  const [emailMeState, emailMe] = useAsyncAction(async () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.healthsharetech.com'
    const url = new URL(location.pathname, origin)
    url.hash = location.hash
    url.search = location.search

    await emailMyself(api, {
      subject: `Your Requested Submission Link`,
      html: `
          <p>
          You requested we send you a direct link to your in-progress submission, so that you can continue on your computer.
          Here is your link for this incident:
          </p>
          <p>
          ${submission.incident.description} on ${formatDateInTimeZone(submission.incident.start_date, { format: 'yyyy-MM-dd' })}
          </p>
          <a href="${url.href}">${url.href}</a>
        `
    })

    return true
  }, [location, api, submission])

  // Get pending expenses for this submission
  const pendingExpenses = useAppSelector((s) =>
    s.expenses.expenses.filter((e) =>
      e.incident_id === submission.incident_id &&
      !e.submission_id
    )
  )

  // Get all attachments for the pending expenses
  const expenseAttachments = useAppSelector((s) =>
    s.attachments.attachments.filter((a) =>
      a.table_name === 'expenses' &&
      pendingExpenses.some((e) => e.id === a.record_id) &&
      a.purpose === 'itemized-bill'
    )
  )

  // Get the blobs for the attachments
  const blobs = useAppSelector((s) =>
    s.blobs.blobs.filter((b) =>
      expenseAttachments.some((a) => a.blob_key === b.key)
    )
  )

  const [downloadState, downloadIndividualBlob] = useAsyncAction(async (blobKey: string) => {
    const blob = blobs.find((b) => b.key === blobKey)
    if (!blob) {
      Toast.error('File not found')
      return
    }

    try {
      // CHM rejects raw image uploads, so itemized bills are converted to PDF
      await downloadBlob(blob, { client, convertImagesToPdf: true })
    } catch (e: any) {
      captureException(e, { tags: { feature: 'chm-download', chm_op: 'download-bill' } })
      Toast.error('Error downloading file: ' + e.message)
    }
  })

  const [downloadAllState, downloadAll] = useAsyncAction(async () => {
    if (blobs.length === 0) {
      Toast.error('No bills to download')
      return
    }

    try {
      const zipFileName = `${submission.incident.description || 'Incident'}_Bills_${formatDateInTimeZone(new Date(), { format: 'yyyy-MM-dd' })}.zip`
      // CHM rejects raw image uploads, so itemized bills are converted to PDF
      await downloadBlobsAsZip(blobs, zipFileName, { client, convertImagesToPdf: true })
    } catch (e: any) {
      captureException(e, { tags: { feature: 'chm-download', chm_op: 'download-zip' } })
      Toast.error('Error creating ZIP file: ' + e.message)
    }
  })

  const handleComplete = () => {
    dispatch(completeSubmission({
      submission: submission,
      attachments: expenseAttachments,
      pendingExpenses: pendingExpenses,
      submittedByUserId: user?.id || '',
    }))
      .finally(() => {
        navigate(`/incidents/${submission.incident_id}`, { replace: true })
      })
  }

  const isAddon = data.type === 'addon'

  return <div className="submitToChm2026-Instructions row">
    <div className="col-12">
      <h2>{isAddon ? 'Submit Add-On to CHM' : 'Submit to CHM'}</h2>
      <p>
        {isAddon
          ? 'You can now submit additional expenses for this incident to CHM.'
          : 'You\'re ready to submit this incident to CHM! Follow the instructions below to enter your information into the CHM portal.'}
      </p>
    </div>

    <div className="col-12 col-lg-6">
      <h4>Instructions</h4>
      <div className="card">
        <div className="card-body">
          <ol>
            <li className="mb-3">
              Log in to the CHM Member Portal at{' '}
              <a href="https://member-portal.chministries.org" target="_blank" rel="noreferrer">
                member-portal.chministries.org
              </a>
            </li>
            <li className="mb-3">
              {isAddon
                ? 'Find your incident and click "Add Bills"'
                : 'Click "Submit Medical Bills" under the "Sharing" menu'}
            </li>
            <li className="mb-3">
              Enter the incident details:
              <ul>
                <li>Patient: {submission.incident.patient_name}</li>
                <li>Date of Birth: {formatDateInTimeZone(submission.incident.patient_dob, { format: 'MM/dd/yyyy' })}</li>
                <li>Incident Date: {formatDateInTimeZone(submission.incident.start_date, { format: 'MM/dd/yyyy' })}</li>
                <li>Description: {submission.incident.description}</li>
              </ul>
            </li>
            <li className="mb-3">
              Upload all your itemized bills (download them below)
            </li>
            <li className="mb-3">
              Enter the expense information for each bill (copy from the list below)
            </li>
            <li>
              Submit the incident in the CHM portal, then click "Mark as Complete" below
            </li>
          </ol>
        </div>
      </div>
    </div>

    <div className="col-12 col-lg-6">
      {mobile && (
        <p className="text-muted small mb-3">
          Prefer submitting on the desktop?{' '}
          {emailMeState.data ? (
            <>
              <span className="text-success">Email sent!</span>{' '}
              <button
                onClick={emailMe}
                className="btn btn-link btn-sm p-0 align-baseline"
                disabled={emailMeState.loading}
              >
                Send again
              </button>
            </>
          ) : (
            <button
              onClick={emailMe}
              className="btn btn-link btn-sm p-0 align-baseline"
              disabled={emailMeState.loading}
            >
              {emailMeState.loading ? 'Sending...' : 'Email me a link to this in-progress submission'}
            </button>
          )}
          {emailMeState.error && (
            <span className="text-danger d-block">{emailMeState.error.message}</span>
          )}
        </p>
      )}

      <h4>Download Bills & Receipts</h4>
      <div className="card">
        <div className="card-body">
          {blobs.length === 0 ? (
            <p className="text-muted mb-0">No bills or receipts attached</p>
          ) : (
            <>
              <div className="mb-3 d-none d-md-block">
                <button
                  className="btn btn-primary w-100"
                  onClick={downloadAll}
                  disabled={downloadAllState.loading}
                >
                  {downloadAllState.loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating ZIP...
                    </>
                  ) : (
                    <>
                      <i className="material-icons me-2" style={{ verticalAlign: 'middle' }}>archive</i>
                      Download All as ZIP ({blobs.length} file{blobs.length !== 1 ? 's' : ''})
                    </>
                  )}
                </button>
              </div>

              <div className="d-none d-md-block border-top pt-3">
                <p className="small text-muted mb-2">Or download individually:</p>
              </div>
              <div className="list-group">
                {blobs.map((blob) => {
                  const attachment = expenseAttachments.find((a) => a.blob_key === blob.key)
                  const expense = pendingExpenses.find((e) => e.id === attachment?.record_id)

                  return (
                    <div key={blob.key} className="list-group-item d-flex justify-content-between align-items-center">
                      <div className="me-2" style={{ minWidth: 0 }}>
                        {/* Image bills are downloaded as PDFs for CHM, so show the eventual file name */}
                        <div className="fw-bold text-break">{downloadFileNameForBlob(blob)}</div>
                        {expense && expense.date && (
                          <small className="text-muted">
                            {expense.provider} - {formatDateInTimeZone(expense.date, { format: 'MM/dd/yyyy' })}
                          </small>
                        )}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary flex-shrink-0"
                        onClick={() => downloadIndividualBlob(blob.key)}
                        disabled={downloadState.loading}
                      >
                        <i className="material-icons" style={{ fontSize: '1.2rem' }}>file_download</i>
                      </button>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <h4 className="mt-4">Itemized Expenses</h4>
      <SubmissionExpenseList expenses={pendingExpenses} />
    </div>

    <div className="col-12 mt-4">
      <div className="row">
        <div className="col-12 col-lg-6 offset-lg-6">
          <button
            onClick={handleComplete}
            className="btn w-100 btn-primary"
          >
            <i className="material-icons me-2" style={{ verticalAlign: 'middle' }}>check_circle</i>
            Mark as Complete
          </button>
        </div>
      </div>
    </div>
  </div>
}
